'use client'

import { useRef, useState, useEffect } from 'react'

/**
 * Lightweight replacement for framer-motion's useInView.
 * Uses IntersectionObserver — zero bundle cost beyond React.
 * Once the element enters view it stays "in view" (one-shot trigger).
 */
export function useInView(options?: IntersectionObserverInit & { once?: boolean }) {
  const ref = useRef<HTMLDivElement>(null)
  const [isInView, setIsInView] = useState(false)
  const once = options?.once ?? true

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          if (once) obs.disconnect()
        } else if (!once) {
          setIsInView(false)
        }
      },
      { threshold: 0.1, ...options }
    )

    obs.observe(el)
    return () => obs.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { ref, isInView }
}

/**
 * Lightweight hook to track scroll progress of a target element.
 * Returns a value between 0 and 1 representing how far the element
 * has scrolled through the viewport.
 */
export function useScrollProgress(targetRef: React.RefObject<HTMLElement | null>) {
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    let rafId: number

    function onScroll() {
      rafId = requestAnimationFrame(() => {
        const el = targetRef.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        const vh = window.innerHeight
        // 0 when at top (not scrolled), 1 when fully scrolled past
        const scrolled = -rect.top
        const raw = scrolled / rect.height
        setProgress(Math.max(0, Math.min(1, raw)))
      })
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll() // initial
    return () => {
      window.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(rafId)
    }
  }, [targetRef])

  return progress
}
